import { Dialog } from '@headlessui/react'
import {
  X,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  TrendingUp,
  Bot,
  MessageSquare,
  Zap,
} from 'lucide-react'
import type { TestChatEvaluationResult } from '@/lib/api/botSettings'

interface TestChatEvaluationModalProps {
  isOpen: boolean
  onClose: () => void
  evaluation: TestChatEvaluationResult | null
  isLoading?: boolean
}

function getScoreColor(score: number) {
  if (score >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200'
  if (score >= 60) return 'text-amber-600 bg-amber-50 border-amber-200'
  return 'text-rose-600 bg-rose-50 border-rose-200'
}

function getOutcomeBadge(outcome: string) {
  switch (outcome) {
    case 'converted':
      return {
        label: 'Venta Concretada 🎉',
        cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      }
    case 'in_progress':
      return {
        label: 'En Negociación ⏳',
        cls: 'bg-blue-100 text-blue-800 border-blue-300',
      }
    case 'escalated':
      return {
        label: 'Escalado a Humano 👤',
        cls: 'bg-purple-100 text-purple-800 border-purple-300',
      }
    case 'lost':
      return {
        label: 'Venta Perdida ❌',
        cls: 'bg-rose-100 text-rose-800 border-rose-300',
      }
    case 'abandoned':
    default:
      return {
        label: 'Abandonada ⚠️',
        cls: 'bg-gray-100 text-gray-800 border-gray-300',
      }
  }
}

export default function TestChatEvaluationModal({
  isOpen,
  onClose,
  evaluation,
  isLoading = false,
}: TestChatEvaluationModalProps) {
  if (!isOpen) return null

  const outcomeBadge = evaluation ? getOutcomeBadge(evaluation.conversionOutcome) : null

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" aria-hidden="true" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-emerald-700 to-[#075E54] px-6 py-4 text-white">
              <div className="flex items-center gap-2.5">
                <div className="rounded-lg bg-white/10 p-2">
                  <Bot className="h-5 w-5 text-emerald-200" />
                </div>
                <div>
                  <Dialog.Title className="text-lg font-bold text-white">
                    Auditoría de Calidad Comercial del Agente
                  </Dialog.Title>
                  <p className="text-xs text-emerald-100">
                    Evaluación automatizada con IA sobre la efectividad del embudo de ventas
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-6 space-y-6">
              {isLoading && (
                <div className="flex flex-col items-center justify-center py-16 text-center space-y-3">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
                  <p className="font-semibold text-gray-700">Analizando conversación con IA...</p>
                  <p className="text-xs text-gray-400 max-w-sm">
                    Revisando progresión en el embudo, claridad en precios, manejo de objeciones y validación de seguridad comercial.
                  </p>
                </div>
              )}

              {!isLoading && !evaluation && (
                <div className="py-12 text-center text-gray-500">
                  <AlertTriangle className="mx-auto h-8 w-8 text-amber-500 mb-2" />
                  <p className="text-sm font-medium">No hay datos de evaluación disponibles.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Envía algunos mensajes en el WebChat de prueba antes de auditar.
                  </p>
                </div>
              )}

              {!isLoading && evaluation && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm">
                      <div className={`flex h-14 w-14 items-center justify-center rounded-xl border text-2xl font-black ${getScoreColor(evaluation.overallScore)}`}>
                        {evaluation.overallScore}
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Puntaje Global</div>
                        <div className="text-sm font-bold text-gray-800">
                          {evaluation.overallScore >= 80 ? 'Excelente Conversión' : evaluation.overallScore >= 60 ? 'Aceptable con Fricción' : 'Requiere Ajustes'}
                        </div>
                        <div className="text-[11px] text-gray-400">Escala de 0 a 100 puntos</div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm flex flex-col justify-center">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Resultado del Turno</div>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${outcomeBadge?.cls}`}>
                          {outcomeBadge?.label}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1">
                        Etapa máxima: <strong className="text-gray-700">{evaluation.quantitative.highestStageReached}</strong>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm flex flex-col justify-center">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Regla Invariante Comercial</div>
                      {evaluation.quantitative.commercialSafetyPassed ? (
                        <div className="flex items-center gap-1.5 text-emerald-700">
                          <ShieldCheck className="h-5 w-5 flex-shrink-0" />
                          <span className="text-xs font-bold">100% Segura (Sin cierre sin producto)</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5 text-rose-600">
                          <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
                          <span className="text-xs font-bold leading-tight">
                            {evaluation.quantitative.commercialSafetyViolation || 'Violación de seguridad comercial'}
                          </span>
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400 mt-1">
                        Total de turnos: <span className="font-semibold text-gray-600">{evaluation.quantitative.turnCount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      Métricas Cuantitativas del Embudo
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                          <span>Progresión de Embudo</span>
                          <span className="font-bold text-gray-800">{evaluation.quantitative.stageProgressionScore}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, Math.max(0, evaluation.quantitative.stageProgressionScore))}%` }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                          <span>Eficiencia de Turnos</span>
                          <span className="font-bold text-gray-800">{evaluation.quantitative.turnEfficiencyScore}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, Math.max(0, evaluation.quantitative.turnEfficiencyScore))}%` }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                          <span>Nivel de Fricción</span>
                          <span className="font-bold text-rose-600">{evaluation.quantitative.frictionScore}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-rose-400 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, Math.max(0, evaluation.quantitative.frictionScore))}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {evaluation.qualitative && (
                    <div className="space-y-4">
                      {evaluation.qualitative.summary && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                          <div className="text-xs font-bold text-blue-900 mb-1 flex items-center gap-1.5">
                            <MessageSquare className="h-4 w-4 text-blue-700" />
                            Diagnóstico Ejecutivo del Asistente
                          </div>
                          <p className="text-xs text-blue-950 leading-relaxed">
                            {evaluation.qualitative.summary}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                          <div className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            Puntos Fuertes Identificados
                          </div>
                          {evaluation.qualitative.strengths.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No se identificaron fortalezas destacadas.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {evaluation.qualitative.strengths.map((item) => (
                                <li key={`strength-${item}`} className="flex items-start gap-1.5 text-xs text-gray-700">
                                  <span className="text-emerald-500 mt-0.5">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-4">
                          <div className="text-xs font-bold text-rose-800 mb-2 flex items-center gap-1.5">
                            <AlertTriangle className="h-4 w-4 text-rose-600" />
                            Fricciones y Objeciones del Cliente
                          </div>
                          {evaluation.qualitative.frictionPoints.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">Sin fricciones detectadas en este flujo.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {evaluation.qualitative.frictionPoints.map((item) => (
                                <li key={`friction-${item}`} className="flex items-start gap-1.5 text-xs text-gray-700">
                                  <span className="text-rose-500 mt-0.5">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {evaluation.qualitative.actionableSuggestions.length > 0 && (
                        <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4">
                          <div className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-1.5">
                            <Zap className="h-4 w-4 text-amber-600" />
                            Sugerencias Accionables para Mejorar la Conversión
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.qualitative.actionableSuggestions.map((sug, idx) => (
                              <li key={`sug-${sug.slice(0, 40)}`} className="flex items-start gap-2 text-xs text-amber-950">
                                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800 flex-shrink-0 mt-0.5">
                                  {idx + 1}
                                </span>
                                <span>{sug}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-gray-100 bg-gray-50 px-6 py-3.5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
              >
                Cerrar Reporte
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  )
}
